import { TicketCategory, TicketCreateInput, TicketEventSource, TicketPatchInput, TicketPriority } from '@navio/nsdk';
import { cn } from 'noddi-ui-common';
import { useState } from 'react';
import { z } from 'zod';

import { NavioOverlayActions, SelectOption, useAppForm } from 'noddi-ui-web';
import { LinkedEntitiesSection } from './components/LinkedEntitiesSection';
import { getLinkKey } from './components/linkKey';
import { LinkEntityType, LinkRef, TicketLinks } from './components/linkTypes';
import { TanstackTicketAssigneePicker } from './components/TanstackTicketAssigneePicker';
import { TanstackTicketServiceDepartmentPicker } from './components/TanstackTicketServiceDepartmentPicker';
import { TicketLinkPickerDialog } from './components/TicketLinkPickerDialog';
import { TICKET_CATEGORY_OPTIONS, TICKET_PRIORITY_OPTIONS } from './meta';

type Props = {
  initialValues?: Partial<TicketFormInitialValues>;
  onSubmit: (payload: TicketCreateInput & TicketPatchInput) => Promise<void>;
  source: TicketEventSource;
  submitLabel?: string;
  className?: string;
  /** Scope the service-department picker + link picker results to these orgs (facility app). */
  serviceOrganizationIds?: number[];
  /** Scope the service-department picker + link picker results to these department IDs (partner app). */
  serviceDepartmentIds?: number[];
};

export type TicketFormInitialValues = {
  title: string;
  description: string;
  category: TicketCategory;
  priority: TicketPriority;
  assignee: SelectOption<number> | undefined;
  serviceDepartment: SelectOption<number> | undefined;
  dueAt: Date | null;
  links: TicketLinks;
};

const validationSchema = z.object({
  title: z.string().min(1, 'Required').max(255, 'Max 255 characters'),
  description: z.string(),
  category: z.string().min(1, 'Required'),
  priority: z.string().min(1, 'Required'),
  assignee: z
    .object({
      value: z.number(),
      label: z.string()
    })
    .optional()
    .nullable(),
  serviceDepartment: z
    .object({
      value: z.number(),
      label: z.string()
    })
    .optional()
    .nullable()
    .refine((val) => !!val, { message: 'Required' }),
  dueAt: z.date().nullable()
});

type FormValues = z.input<typeof validationSchema>;

const FORM_ID = 'ticket-form';

const EMPTY_LINKS: TicketLinks = {
  userGroup: null,
  userGroupCar: null,
  booking: null,
  tireQuote: null,
  storageObjectGroup: null
};

export const TicketForm = ({
  initialValues,
  onSubmit,
  source,
  submitLabel = 'Save',
  className,
  serviceOrganizationIds,
  serviceDepartmentIds
}: Props) => {
  const [links, setLinks] = useState<TicketLinks>(initialValues?.links ?? EMPTY_LINKS);
  const [pickerOpen, setPickerOpen] = useState(false);

  const defaultValues: FormValues = {
    title: initialValues?.title ?? '',
    description: initialValues?.description ?? '',
    category: initialValues?.category ?? TicketCategory.FOLLOW_UP,
    priority: initialValues?.priority ?? TicketPriority.NORMAL,
    assignee: initialValues?.assignee,
    serviceDepartment: initialValues?.serviceDepartment,
    dueAt: initialValues?.dueAt ?? null
  };

  const form = useAppForm({
    defaultValues,
    validators: {
      onSubmit: validationSchema
    },
    onSubmit: async ({ value }) => {
      const payload: TicketCreateInput & TicketPatchInput = {
        title: value.title,
        description: value.description,
        category: value.category as TicketCategory,
        priority: value.priority as TicketPriority,
        source,
        serviceDepartmentId: value.serviceDepartment!.value,
        assigneeId: value.assignee?.value ?? null,
        userGroupId: links.userGroup?.id ?? null,
        userGroupCarId: links.userGroupCar?.id ?? null,
        bookingId: links.booking?.id ?? null,
        tireQuoteId: links.tireQuote?.id ?? null,
        storageObjectGroupId: links.storageObjectGroup?.id ?? null,
        dueAt: value.dueAt ? value.dueAt.toISOString() : null
      };

      await onSubmit(payload);
    }
  });

  const handlePickLink = (entityType: LinkEntityType, ref: LinkRef) => {
    setLinks((prev) => ({ ...prev, [getLinkKey(entityType)]: ref }));
  };

  const handleRemoveLink = (entityType: LinkEntityType) => {
    setLinks((prev) => ({ ...prev, [getLinkKey(entityType)]: null }));
  };

  return (
    <form.AppForm>
      <form
        id={FORM_ID}
        className={cn('flex flex-col gap-5', className)}
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}
      >
        <form.AppField name='title'>
          {/* oxlint-disable-next-line jsx-a11y/no-autofocus -- first field of a ticket form the user opened deliberately */}
          {(field) => <field.Input label='Title' placeholder='Short headline' autoFocus />}
        </form.AppField>

        <form.AppField name='description'>
          {(field) => (
            <field.TextArea label='Description (optional)' rows={4} placeholder='Add context for this ticket...' />
          )}
        </form.AppField>

        <form.AppField name='serviceDepartment'>
          {() => (
            <TanstackTicketServiceDepartmentPicker
              serviceOrganizationIds={serviceOrganizationIds}
              serviceDepartmentIds={serviceDepartmentIds}
            />
          )}
        </form.AppField>

        <form.AppField name='category'>
          {(field) => <field.Select<string> label='Category' options={TICKET_CATEGORY_OPTIONS} />}
        </form.AppField>

        <form.AppField name='priority'>
          {(field) => <field.Select<string> label='Priority' options={TICKET_PRIORITY_OPTIONS} />}
        </form.AppField>

        <form.AppField name='assignee'>{() => <TanstackTicketAssigneePicker />}</form.AppField>

        <form.AppField name='dueAt'>
          {(field) => <field.DateTimePicker label='Due date (optional)' showClearButton />}
        </form.AppField>

        <LinkedEntitiesSection links={links} onAdd={() => setPickerOpen(true)} onRemove={handleRemoveLink} />

        <NavioOverlayActions>
          <form.SubscribeButton formId={FORM_ID} label={submitLabel} />
        </NavioOverlayActions>

        <TicketLinkPickerDialog
          open={pickerOpen}
          onOpenChange={setPickerOpen}
          links={links}
          onPick={handlePickLink}
          serviceDepartmentIds={serviceDepartmentIds}
        />
      </form>
    </form.AppForm>
  );
};
